import { useMemo } from "react";
import { t } from "ttag";

import { formatNumber } from "metabase/lib/formatting/numbers";
import type { Download } from "metabase-types/store";

import StatusLarge from "../StatusLarge";
import { isCompleted, isErrored, isInProgress } from "../utils/downloads";

export interface DownloadsStatusLargeProps {
  downloads: Download[];
  onDismiss: () => void;
  onCollapse: () => void;
}

export const DownloadsStatusLarge = ({
  downloads,
  onDismiss,
  onCollapse,
}: DownloadsStatusLargeProps) => {
  const status = useMemo(() => {
    return {
      title: getTitle(downloads),
      variant: getStatusVariant(downloads),
      items: downloads.map((download) => ({
        id: download.id,
        title: download.title,
        icon: "download",
        description: getDownloadDescription(download),
        isInProgress: isInProgress(download),
        isCompleted: isCompleted(download) && !download.truncated,
        variant: download.truncated
          ? ("warning" as const)
          : isErrored(download)
            ? ("error" as const)
            : undefined,
        isAborted: isErrored(download),
      })),
    };
  }, [downloads]);

  const hasInProgress = downloads.some(isInProgress);

  return (
    <StatusLarge
      isActive
      status={status}
      onCollapse={hasInProgress ? onCollapse : undefined}
      onDismiss={hasInProgress ? undefined : onDismiss}
    />
  );
};

const getTitle = (downloads: Download[]): string => {
  const isDone = downloads.every(isCompleted);
  const isError = downloads.some(isErrored);
  const isTruncated = downloads.some(({ truncated }) => truncated);

  if (isError) {
    return t`Download error`;
  } else if (isTruncated) {
    return t`Warning`;
  } else if (isDone) {
    return t`Done!`;
  } else {
    return t`Downloading…`;
  }
};

const getStatusVariant = (downloads: Download[]) => {
  if (downloads.some(isErrored)) {
    return "error" as const;
  } else if (downloads.some(({ truncated }) => truncated)) {
    return "warning" as const;
  }
};

const getDownloadDescription = (download: Download): string => {
  const isDone = isCompleted(download);
  const isError = isErrored(download);

  if (isError) {
    return download.error ?? t`Download failed`;
  } else if (isDone) {
    if (download.truncated && download.rowLimit != null) {
      const rowLimit = formatNumber(download.rowLimit);
      return `${t`Data truncated to ${rowLimit} rows.`} ${t`Showing first ${rowLimit} rows`}`;
    }
    return t`Download completed`;
  } else {
    return "";
  }
};
